import React from 'react';
import styled from 'styled-components';
import { 
  Upload, 
  Brain, 
  FileText, 
  Settings, 
  Award, 
  CheckCircle,
  Circle,
  ArrowRight,
  ArrowLeft,
  Target,
  BarChart3,
  Zap
} from 'lucide-react';
import { theme } from '../styles/theme';
import { useTour } from '../context/TourContext';
import { onboardingTourSteps } from '../data/tourSteps';

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const StepItem = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${({ isActive, isCompleted }) => 
    isCompleted ? theme.colors.success[500] :
    isActive ? theme.colors.primary[400] : 
    theme.colors.border
  };
  background: ${({ isActive, isCompleted }) => 
    isCompleted ? theme.colors.success[100] :
    isActive ? theme.colors.primary[50] : 
    theme.colors.surface
  };
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    border-color: ${({ isCompleted }) => 
      isCompleted ? theme.colors.success[600] : theme.colors.primary[500]
    };
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
`;

const StepIcon = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ isActive, isCompleted }) => 
    isCompleted ? theme.colors.success[500] :
    isActive ? theme.colors.primary[500] : 
    theme.colors.border
  };
  color: ${({ isActive, isCompleted }) => 
    isCompleted || isActive ? 'white' : theme.colors.textMuted
  };
  flex-shrink: 0;
  transition: all ${theme.transitions.base};
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3<{ isActive: boolean; isCompleted: boolean }>`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${({ isActive, isCompleted }) => 
    isCompleted ? theme.colors.success[700] :
    isActive ? theme.colors.primary[700] : 
    theme.colors.textPrimary
  };
  margin-bottom: ${theme.spacing[1]};
`;

const StepDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  margin: 0;
  line-height: ${theme.typography.lineHeight.normal};
`;

const StepNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing[6]};
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border};
`;

const NavButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  border: 1px solid;

  ${({ variant }) => variant === 'primary' ? `
    background: ${theme.colors.primary[500]};
    color: white;
    border-color: ${theme.colors.primary[500]};

    &:hover {
      background: ${theme.colors.primary[600]};
      border-color: ${theme.colors.primary[600]};
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.md};
    }

    &:disabled {
      background: ${theme.colors.border};
      border-color: ${theme.colors.border};
      color: ${theme.colors.textMuted};
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: ${theme.colors.surface};
    color: ${theme.colors.textSecondary};
    border-color: ${theme.colors.border};

    &:hover {
      background: ${theme.colors.primary[50]};
      color: ${theme.colors.primary[600]};
      border-color: ${theme.colors.primary[300]};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `}
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${theme.colors.border};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${theme.spacing[6]};
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.primary[500]}, ${theme.colors.primary[400]});
  border-radius: ${theme.borderRadius.full};
  transition: width ${theme.transitions.slow};
  width: ${({ progress }) => progress}%;
`;

const ProgressText = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing[3]};
  text-align: center;
`;

interface OnboardingStepsProps {
  currentStep: number;
  onStepChange: (step: number) => void;
}

const onboardingSteps = [
  {
    id: 'welcome',
    title: 'Welcome & Platform Overview',
    description: 'Learn about OpenTender AI and how it can transform your tender process',
    icon: <Target size={20} />,
    completed: false
  },
  {
    id: 'upload',
    title: 'Upload Your First Document',
    description: 'Try uploading a tender document to see our AI in action',
    icon: <Upload size={20} />,
    completed: false
  },
  {
    id: 'analysis',
    title: 'Understanding AI Analysis',
    description: 'Discover how our AI extracts requirements and evaluates content',
    icon: <Brain size={20} />,
    completed: false
  },
  {
    id: 'results',
    title: 'Reviewing Results',
    description: 'Learn to interpret analysis results and generated responses',
    icon: <BarChart3 size={20} />,
    completed: false
  },
  {
    id: 'features',
    title: 'Exploring Advanced Features',
    description: 'Discover tender bank, templates, and collaboration tools',
    icon: <Zap size={20} />,
    completed: false
  },
  {
    id: 'preferences',
    title: 'Setting Up Preferences',
    description: 'Customize your workspace and notification settings',
    icon: <Settings size={20} />,
    completed: false
  }
];

const OnboardingSteps: React.FC<OnboardingStepsProps> = ({ currentStep, onStepChange }) => {
  const progress = ((currentStep + 1) / onboardingSteps.length) * 100;
  const { startTour } = useTour();

  const handleStepClick = (stepIndex: number) => {
    onStepChange(stepIndex);
  };

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      onStepChange(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      onStepChange(currentStep - 1);
    }
  };

  const handleStartTour = () => {
    startTour(onboardingTourSteps);
  };

  return (
    <StepsContainer>
      <div>
        <ProgressText>
          Step {currentStep + 1} of {onboardingSteps.length}
        </ProgressText>
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>
      </div>

      <StepsList>
        {onboardingSteps.map((step, index) => (
          <StepItem
            key={step.id}
            isActive={index === currentStep}
            isCompleted={index < currentStep}
            onClick={() => handleStepClick(index)}
          >
            <StepIcon 
              isActive={index === currentStep} 
              isCompleted={index < currentStep}
            >
              {index < currentStep ? <CheckCircle size={20} /> : step.icon}
            </StepIcon>
            <StepContent>
              <StepTitle 
                isActive={index === currentStep} 
                isCompleted={index < currentStep}
              >
                {step.title}
              </StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </StepContent>
            {index === currentStep && (
              <ArrowRight size={16} color={theme.colors.primary[500]} />
            )}
          </StepItem>
        ))}
      </StepsList>

      <StepNavigation>
        <NavButton 
          variant="secondary" 
          onClick={handlePrevious}
          disabled={currentStep === 0}
        >
          <ArrowLeft size={16} />
          Previous
        </NavButton>

        <NavButton 
          variant="primary" 
          onClick={handleStartTour}
        >
          Start Interactive Tour
          <Award size={16} />
        </NavButton>

        <NavButton 
          variant="secondary" 
          onClick={handleNext}
          disabled={currentStep === onboardingSteps.length - 1}
        >
          Next
          <ArrowRight size={16} />
        </NavButton>
      </StepNavigation>
    </StepsContainer>
  );
};

export default OnboardingSteps;